import React, { useState } from 'react';
import {
  CharacterMode,
  DriveMode,
  EngineProfileId,
  SpeedUnit,
  TransmissionMode,
  VehicleState,
} from '../types';
import { SpeedometerHub } from './speedometers/SpeedometerHub';
import { GPSStatus } from './GPSStatus';
import { SettingsDrawer } from './SettingsDrawer';
import {
  Power,
  Volume2,
  VolumeX,
  User,
  ShieldAlert,
  Flame,
  Zap,
  Sliders,
  ChevronRight,
} from 'lucide-react';
import { ENGINE_PROFILES, isEVProfile } from '../config/engines';
import { EV_SOUND_PROFILES } from '../config/evProfiles';
import { ApiUser } from '../services/api';

interface DrivingDashboardProps {
  vehicleState: VehicleState;
  speedUnit: SpeedUnit;
  onSetSpeedUnit: (unit: SpeedUnit) => void;
  onStartEngine: () => void;
  onStopEngine: () => void;
  onShiftUp: () => void;
  onShiftDown: () => void;
  onSetTransmissionMode: (mode: TransmissionMode) => void;
  onSetDriveMode: (mode: DriveMode) => void;
  onSetEngineProfile: (profileId: EngineProfileId) => void;
  onSetCharacterMode: (mode: CharacterMode) => void;
  onSetVolume: (vol: number) => void;
  onToggleMute: () => void;
  currentUser: ApiUser | null;
  onOpenCatalogue: () => void;
  onOpenAccount: () => void;
  onOpenAdmin: () => void;
}

export const DrivingDashboard: React.FC<DrivingDashboardProps> = ({
  vehicleState,
  speedUnit,
  onSetSpeedUnit,
  onStartEngine,
  onStopEngine,
  onShiftUp,
  onShiftDown,
  onSetTransmissionMode,
  onSetDriveMode,
  onSetEngineProfile,
  onSetCharacterMode,
  onSetVolume,
  onToggleMute,
  currentUser,
  onOpenCatalogue,
  onOpenAccount,
  onOpenAdmin,
}) => {
  const [isSettingsOpen, setIsSettingsOpen] = useState<boolean>(false);
  const isEV = isEVProfile(vehicleState.engineProfile);
  const isAdmin = currentUser?.role === 'ADMIN' || currentUser?.role === 'SUPER_ADMIN';

  // Resolve current sound profile metadata safely
  const currentSoundName = isEV
    ? (EV_SOUND_PROFILES as Record<string, { name: string }>)[vehicleState.engineProfile]?.name || 'EV Soundscape'
    : (ENGINE_PROFILES as Record<string, { name: string }>)[vehicleState.engineProfile]?.name || 'Combustion Engine';

  // Display speed in current unit
  const displaySpeed =
    speedUnit === 'mph' ? vehicleState.speedMph : vehicleState.speedKmh;

  return (
    <div className="relative w-screen h-screen flex flex-col justify-between bg-[#06070a] text-slate-100 overflow-hidden select-none p-3 sm:p-5">
      {/* Dynamic Background Glow reacting to Drive Mode and Speed */}
      <div
        className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[720px] h-[720px] rounded-full blur-[180px] pointer-events-none transition-all duration-700 ${
          isEV
            ? vehicleState.mode === 'SPORT'
              ? 'bg-cyan-500/18 scale-110'
              : vehicleState.mode === 'ECO'
              ? 'bg-emerald-500/14'
              : 'bg-indigo-500/14'
            : vehicleState.mode === 'SPORT'
            ? 'bg-rose-600/20 scale-115'
            : vehicleState.mode === 'ECO'
            ? 'bg-emerald-600/12'
            : 'bg-amber-600/14'
        }`}
      />

      {/* Admin Simulation Banner (if simulation active) */}
      {vehicleState.isDemoMode && isAdmin && (
        <div className="relative z-30 flex items-center justify-between px-4 py-1.5 bg-amber-950/70 border border-amber-500/40 rounded-xl text-xs text-amber-300 backdrop-blur-md mb-2">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            <span className="font-mono font-bold tracking-wider uppercase">
              Admin Simulation Mode Active
            </span>
            <span className="text-amber-200/70 hidden sm:inline">
              • Speed: {Math.round(vehicleState.speedKmh)} km/h • Effort:{' '}
              {Math.round(vehicleState.effort * 100)}%
            </span>
          </div>
          <button
            type="button"
            onClick={onOpenAdmin}
            className="px-3 py-1 bg-amber-500 hover:bg-amber-400 text-slate-950 font-black uppercase tracking-wider rounded-lg text-[10px] transition-colors cursor-pointer"
          >
            Admin Controls
          </button>
        </div>
      )}

      {/* TOP AUTOMOTIVE STATUS & NAV BAR */}
      <header className="relative z-20 flex items-center justify-between gap-3 w-full">
        {/* Left: Branding & GPS Status */}
        <div className="flex items-center gap-3 shrink-0">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-red-600 shadow-[0_0_10px_rgba(220,38,38,0.9)] animate-pulse" />
            <span className="font-black tracking-[0.22em] text-lg sm:text-xl font-display text-white">
              REVIVE
            </span>
          </div>

          <GPSStatus
            status={vehicleState.gpsStatus}
            accuracy={vehicleState.gpsAccuracy}
          />
        </div>

        {/* Center: Sound Profile Quick Switch & Catalogue Trigger */}
        <div className="flex items-center justify-center">
          <button
            type="button"
            onClick={onOpenCatalogue}
            className="group flex items-center gap-2.5 px-4 py-2 rounded-2xl bg-slate-900/90 hover:bg-slate-800/90 border border-slate-800/90 hover:border-cyan-500/50 transition-all shadow-lg backdrop-blur-md cursor-pointer"
          >
            <div
              className={`p-1.5 rounded-xl ${
                isEV
                  ? 'bg-cyan-500/20 text-cyan-400'
                  : 'bg-rose-500/20 text-rose-400'
              }`}
            >
              {isEV ? <Zap className="w-4 h-4" /> : <Flame className="w-4 h-4" />}
            </div>
            <div className="flex flex-col text-left">
              <span className="text-[10px] font-mono tracking-widest uppercase text-slate-400">
                {isEV ? 'EV Soundscape' : 'Engine Sound'}
              </span>
              <span className="text-xs sm:text-sm font-black tracking-wide text-white group-hover:text-cyan-300 transition-colors">
                {currentSoundName}
              </span>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-cyan-400 transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>

        {/* Right: Unit Toggle, Audio, Account, and Admin */}
        <div className="flex items-center gap-2 shrink-0">
          {/* Unit Toggle: KM/H vs MPH */}
          <button
            type="button"
            onClick={() => onSetSpeedUnit(speedUnit === 'kmh' ? 'mph' : 'kmh')}
            className="px-2.5 py-1.5 rounded-xl bg-slate-900/80 border border-slate-800 hover:border-slate-700 text-[11px] font-mono font-bold text-slate-300 uppercase transition-all cursor-pointer"
            title="Toggle Speed Unit"
          >
            {speedUnit.toUpperCase()}
          </button>

          {/* Mute Button */}
          <button
            type="button"
            onClick={onToggleMute}
            className={`p-2.5 rounded-xl border transition-all cursor-pointer ${
              vehicleState.isMuted
                ? 'bg-rose-950/60 border-rose-500/60 text-rose-400 shadow-[0_0_12px_rgba(244,63,94,0.3)]'
                : 'bg-slate-900/80 border-slate-800 text-slate-300 hover:text-white'
            }`}
            title={vehicleState.isMuted ? 'Unmute Audio' : 'Mute Audio'}
          >
            {vehicleState.isMuted ? (
              <VolumeX className="w-4 h-4 sm:w-5 sm:h-5" />
            ) : (
              <Volume2 className="w-4 h-4 sm:w-5 sm:h-5" />
            )}
          </button>

          {/* User Account / Profile */}
          <button
            type="button"
            onClick={onOpenAccount}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-900/80 hover:bg-slate-800 border border-slate-800 hover:border-slate-700 text-slate-300 hover:text-white text-xs font-semibold transition-all cursor-pointer"
            title="User Account"
          >
            <User className="w-4 h-4 text-slate-400" />
            <span className="hidden md:inline max-w-[100px] truncate">
              {currentUser ? currentUser.displayName || currentUser.email : 'Guest'}
            </span>
          </button>

          {/* Admin Portal Button (Strictly Rendered for Admin/SuperAdmin) */}
          {isAdmin && (
            <button
              type="button"
              onClick={onOpenAdmin}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-rose-950/60 hover:bg-rose-900/80 border border-rose-600/60 text-rose-300 hover:text-white text-xs font-black uppercase tracking-wider transition-all cursor-pointer shadow-[0_0_15px_rgba(225,29,72,0.3)]"
              title="Open Admin Portal"
            >
              <ShieldAlert className="w-4 h-4 text-rose-400" />
              <span className="hidden sm:inline">Admin</span>
            </button>
          )}

          {/* Fine Audio Settings Button */}
          <button
            type="button"
            onClick={() => setIsSettingsOpen(true)}
            className="p-2.5 rounded-xl bg-slate-900/80 border border-slate-800 text-slate-300 hover:text-white hover:border-slate-700 transition-all cursor-pointer"
            title="Audio & Powertrain Settings"
          >
            <Sliders className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>
      </header>

      {/* CENTER STAGE: ANIME.JS SPEEDOMETERS (6 DEDICATED ARCHITECTURES) */}
      <main className="relative z-10 flex-1 flex flex-col items-center justify-center my-auto w-full max-w-5xl mx-auto py-2">
        <SpeedometerHub
          speed={displaySpeed}
          speedUnit={speedUnit}
          acceleration={vehicleState.acceleration}
          rpm={vehicleState.currentRpm}
          gear={vehicleState.gear}
          isEV={isEV}
          driveMode={vehicleState.mode}
          maxSpeed={speedUnit === 'mph' ? 160 : 260}
        />
      </main>

      {/* BOTTOM CONTROL DECK: Large 56-72px automotive touch targets */}
      <footer className="relative z-20 w-full max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
        {/* Drive Mode Selector (ECO / COMFORT / SPORT) - 56-64px touch targets */}
        <div className="flex items-center bg-slate-900/90 border border-slate-800/90 p-1.5 rounded-2xl shadow-xl backdrop-blur-md w-full sm:w-auto justify-around">
          <button
            type="button"
            onClick={() => onSetDriveMode('ECO')}
            className={`flex-1 sm:flex-initial min-w-[90px] md:min-w-[110px] h-12 md:h-14 rounded-xl flex flex-col items-center justify-center text-xs md:text-sm font-black tracking-widest uppercase transition-all duration-200 cursor-pointer ${
              vehicleState.mode === 'ECO'
                ? 'bg-emerald-600 text-white shadow-[0_0_20px_rgba(16,185,129,0.5)] scale-[1.02]'
                : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
            }`}
          >
            <span>ECO</span>
            <span className="text-[9px] font-mono tracking-wider opacity-70">
              {isEV ? 'Efficiency' : 'Low RPM'}
            </span>
          </button>

          <button
            type="button"
            onClick={() => onSetDriveMode('COMFORT')}
            className={`flex-1 sm:flex-initial min-w-[90px] md:min-w-[110px] h-12 md:h-14 rounded-xl flex flex-col items-center justify-center text-xs md:text-sm font-black tracking-widest uppercase transition-all duration-200 cursor-pointer ${
              vehicleState.mode === 'COMFORT'
                ? 'bg-cyan-600 text-white shadow-[0_0_20px_rgba(6,182,212,0.5)] scale-[1.02]'
                : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
            }`}
          >
            <span>COMFORT</span>
            <span className="text-[9px] font-mono tracking-wider opacity-70">
              {isEV ? 'Harmonic' : 'Balanced'}
            </span>
          </button>

          <button
            type="button"
            onClick={() => onSetDriveMode('SPORT')}
            className={`flex-1 sm:flex-initial min-w-[90px] md:min-w-[110px] h-12 md:h-14 rounded-xl flex flex-col items-center justify-center text-xs md:text-sm font-black tracking-widest uppercase transition-all duration-200 cursor-pointer ${
              vehicleState.mode === 'SPORT'
                ? 'bg-rose-600 text-white shadow-[0_0_24px_rgba(225,29,72,0.6)] scale-[1.02]'
                : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
            }`}
          >
            <span>SPORT</span>
            <span className="text-[9px] font-mono tracking-wider opacity-70">
              {isEV ? 'Dynamic' : 'Max Revs'}
            </span>
          </button>
        </div>

        {/* Manual Paddle Shifter Controls when in ICE mode */}
        {!isEV && (
          <div className="hidden lg:flex items-center gap-1.5 bg-slate-900/80 border border-slate-800/80 p-1.5 rounded-2xl">
            <button
              type="button"
              onClick={onShiftDown}
              className="px-3 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-mono font-bold uppercase transition-all active:scale-95 cursor-pointer"
              title="Shift Down"
            >
              - G
            </button>
            <span className="font-mono text-xs font-bold text-amber-400 px-2">
              G{vehicleState.gear}
            </span>
            <button
              type="button"
              onClick={onShiftUp}
              className="px-3 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-mono font-bold uppercase transition-all active:scale-95 cursor-pointer"
              title="Shift Up"
            >
              + G
            </button>
          </div>
        )}

        {/* Primary CTA: START / STOP SOUND (Large Automotive Target 56-64px) */}
        <div className="flex items-center gap-3 w-full sm:w-auto justify-center">
          {!vehicleState.isEngineRunning ? (
            <button
              type="button"
              onClick={onStartEngine}
              className={`flex-1 sm:flex-initial min-w-[200px] md:min-w-[240px] h-14 md:h-16 px-6 rounded-2xl font-black text-sm md:text-base uppercase tracking-widest transition-all duration-200 cursor-pointer flex items-center justify-center gap-3 shadow-xl active:scale-95 ${
                isEV
                  ? 'bg-cyan-500 hover:bg-cyan-400 text-slate-950 shadow-[0_0_30px_rgba(6,182,212,0.4)]'
                  : 'bg-red-600 hover:bg-red-500 text-white shadow-[0_0_30px_rgba(220,38,38,0.5)]'
              }`}
            >
              <Power className="w-5 h-5 animate-pulse" />
              <span>{isEV ? 'START EV SOUND' : 'START ENGINE'}</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={onStopEngine}
              className="flex-1 sm:flex-initial min-w-[200px] md:min-w-[240px] h-14 md:h-16 px-6 rounded-2xl font-black text-sm md:text-base uppercase tracking-widest transition-all duration-200 cursor-pointer flex items-center justify-center gap-3 bg-red-950/80 hover:bg-red-900 border-2 border-red-600/70 text-red-300 hover:text-white shadow-[0_0_30px_rgba(220,38,38,0.3)] active:scale-95"
            >
              <Power className="w-5 h-5" />
              <span>{isEV ? 'STOP EV SOUND' : 'STOP ENGINE'}</span>
            </button>
          )}
        </div>
      </footer>

      {/* Off-canvas Settings Drawer */}
      <SettingsDrawer
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        vehicleState={vehicleState}
        speedUnit={speedUnit}
        onSetSpeedUnit={onSetSpeedUnit}
        onSelectEngine={onSetEngineProfile}
        onSelectCharacter={onSetCharacterMode}
        onSelectTransmission={onSetTransmissionMode}
        onSetVolume={onSetVolume}
        onToggleMute={onToggleMute}
      />
    </div>
  );
};
